import React, { useState, useEffect } from 'react';
import { Question, WritingSuggestion } from '../../../../types';
import { apiService } from '../../../../services';
import WritingSuggestions from '../WritingSuggestions';
import styles from './AdditionalQuestions.module.css';

interface AdditionalQuestionsProps {
  industryId: string;
  roleId: string;
  initialResponses: Array<{
    questionId: string;
    questionText: string;
    answer: string;
    category: string;
  }>;
  savedQuestions?: Question[]; // ← NEW: Saved questions from localStorage
  savedAnswers?: Record<string, string>; // ← NEW: Saved answers from localStorage
  onQuestionsGenerated: (questions: Question[]) => void;
  onAnswersChange: (answers: Record<string, string>) => void;
  shouldGenerate?: boolean;
}

const AdditionalQuestions: React.FC<AdditionalQuestionsProps> = ({
  industryId,
  roleId,
  initialResponses,
  savedQuestions = [], // ← Receive saved questions from parent
  savedAnswers = {}, // ← Receive saved answers from parent
  onQuestionsGenerated,
  onAnswersChange,
  shouldGenerate = false
}) => {
  const [questions, setQuestions] = useState<Question[]>(savedQuestions);
  const [answers, setAnswers] = useState<Record<string, string>>(savedAnswers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skippedQuestions, setSkippedQuestions] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedHints, setExpandedHints] = useState<Record<string, boolean>>({});
  const questionsPerPage = 3;

  useEffect(() => {
    // 🔥 CRITICAL: Check if questions already exist in localStorage
    // Only generate if we don't have saved questions AND shouldGenerate is true
    if (savedQuestions && savedQuestions.length > 0) {
      console.log('📦 AdditionalQuestions: Restoring from localStorage (no API call)');
      setQuestions(savedQuestions);
      setAnswers(savedAnswers);
      setLoading(false);
      return; // Don't call API
    }
    
    if (shouldGenerate && questions.length === 0) {
      console.log('🤖 AdditionalQuestions: Generating with AI (first time)');
      generateQuestions();
    }
  }, [shouldGenerate, savedQuestions, savedAnswers]);

  const generateQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const questionSet = await apiService.generateAdditionalQuestions(
        industryId,
        roleId,
        initialResponses
      );
      
      setQuestions(questionSet.questions);
      onQuestionsGenerated(questionSet.questions);
      
      // Initialize answers
      const initialAnswers: Record<string, string> = {};
      questionSet.questions.forEach((q: Question) => {
        initialAnswers[q.id] = '';
      });
      setAnswers(initialAnswers);
      onAnswersChange(initialAnswers);
      
    } catch (err) {
      console.error('Error generating additional questions:', err);
      setError('Failed to generate additional questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    const newAnswers = {
      ...answers,
      [questionId]: answer
    };
    setAnswers(newAnswers);
    onAnswersChange(newAnswers);
  };

  const handleSkipQuestion = (questionId: string) => {
    const newSkipped = new Set(skippedQuestions);
    if (newSkipped.has(questionId)) {
      newSkipped.delete(questionId);
    } else {
      newSkipped.add(questionId);
    }
    setSkippedQuestions(newSkipped);
  };

  const toggleHints = (questionId: string) => {
    setExpandedHints(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getWordCount = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const getQuestionIcon = (category: string): string => {
    const iconMap: Record<string, string> = {
      'process_optimization': 'cogs',
      'technology_integration': 'laptop-code',
      'workflow_automation': 'robot',
      'data_management': 'database',
      'team_collaboration': 'users',
      'performance_metrics': 'chart-line',
      'customer_satisfaction': 'smile',
      'cost_reduction': 'dollar-sign',
      'quality_improvement': 'award',
      'scalability_planning': 'expand-arrows-alt',
      'risk_mitigation': 'shield-alt',
      'training_development': 'graduation-cap',
      'compliance_enhancement': 'check-circle',
      'vendor_management': 'handshake',
      'change_management': 'exchange-alt'
    };
    return iconMap[category] || 'question-circle';
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}>
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Generando preguntas personalizadas basadas en tus respuestas...</p>
        <p className={styles.loadingSubtext}>Esto puede tomar unos momentos</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <i className="fas fa-exclamation-triangle"></i>
        <p>{error}</p>
        <button onClick={generateQuestions} className={styles.retryButton}>
          <i className="fas fa-redo"></i>
          Intentar de Nuevo
        </button>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className={styles.noQuestions}>
        <i className="fas fa-info-circle"></i>
        <p>No hay preguntas adicionales disponibles en este momento.</p>
      </div>
    );
  }

  // Pagination logic
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const startIndex = currentPage * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = questions.slice(startIndex, endIndex);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>
          <i className="fas fa-magic"></i>
          Preguntas Detalladas Personalizadas
        </h3>
        <p>Basado en tus respuestas iniciales, hemos generado {questions.length} preguntas específicas</p>
      </div>

      {/* Progress Bar */}
      <div className={styles.progressBar}>
        <div className={styles.progressInfo}>
          <span>Página {currentPage + 1} de {totalPages}</span>
          <span>{Object.values(answers).filter(answer => answer.trim().length > 0).length} / {questions.length} respondidas</span>
        </div>
        <div className={styles.progressTrack}>
          <div 
            className={styles.progressFill}
            style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
          />
        </div>
      </div>

      <div className={styles.questionsList}>
        {currentQuestions.map((question, index) => {
          const globalIndex = startIndex + index;
          return (
          <div 
            key={question.id} 
            className={`${styles.questionCard} ${skippedQuestions.has(question.id) ? styles.skipped : ''}`}
          >
            <div className={styles.questionHeader}>
              <div className={styles.questionNumber}>
                <i className={`fas fa-${getQuestionIcon(question.category)}`}></i>
                <span>Pregunta {globalIndex + 1}</span>
              </div>
              <div className={styles.questionActions}>
                <button
                  onClick={() => handleSkipQuestion(question.id)}
                  className={`${styles.actionButton} ${styles.skipButton} ${
                    skippedQuestions.has(question.id) ? styles.active : ''
                  }`}
                >
                  <i className="fas fa-eye-slash"></i>
                  {skippedQuestions.has(question.id) ? 'Restaurar' : 'Omitir'}
                </button>
              </div>
            </div>

            <div className={styles.questionText}>
              {question.text}
            </div>

            {question.hints && question.hints.length > 0 && (
              <div className={styles.hintsContainer}>
                <button 
                  className={styles.hintsToggle}
                  onClick={() => toggleHints(question.id)}
                  type="button"
                >
                  <i className={`fas fa-lightbulb ${styles.hintIcon}`}></i>
                  <span>Sugerencias para responder</span>
                  <i className={`fas fa-chevron-${expandedHints[question.id] ? 'up' : 'down'}`}></i>
                </button>
                {expandedHints[question.id] && (
                  <div className={styles.hints}>
                    <ul>
                      {question.hints.map((hint: string, hintIndex: number) => (
                        <li key={hintIndex}>{hint}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!skippedQuestions.has(question.id) && (
              <div className={styles.answerSection}>
                <label htmlFor={`answer-${question.id}`}>
                  Tu Respuesta:
                </label>
                <textarea
                  id={`answer-${question.id}`}
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="Por favor proporciona una respuesta detallada..."
                  className={styles.answerInput}
                  rows={4}
                />
                
                <div className={styles.answerMeta}>
                  <div className={styles.wordCount}>
                    <i className="fas fa-font"></i>
                    {getWordCount(answers[question.id] || '')} palabras
                    {question.minWords && (
                      <span className={styles.minWords}>
                        (recomendado: {question.minWords})
                      </span>
                    )}
                  </div>
                  {question.isRequired && (
                    <div className={styles.required}>
                      <i className="fas fa-asterisk"></i>
                      Requerido
                    </div>
                  )}
                </div>

                {/* AI Writing Suggestions */}
                <WritingSuggestions
                  questionId={question.id}
                  industryId={industryId}
                  roleId={roleId}
                  currentAnswer={answers[question.id] || ''}
                  onSuggestionClick={(suggestion: WritingSuggestion) => {
                    // Append suggestion content to current answer
                    const currentAnswer = answers[question.id] || '';
                    const newAnswer = currentAnswer 
                      ? `${currentAnswer}\n\n${suggestion.content}`
                      : suggestion.content;
                    handleAnswerChange(question.id, newAnswer);
                  }}
                />
              </div>
            )}

            {skippedQuestions.has(question.id) && (
              <div className={styles.skippedMessage}>
                <i className="fas fa-eye-slash"></i>
                <span>Esta pregunta ha sido omitida</span>
              </div>
            )}
          </div>
        );
        })}
      </div>

      {/* Pagination Controls */}
      <div className={styles.pagination}>
        <button 
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          className={styles.paginationButton}
        >
          <i className="fas fa-chevron-left"></i>
          Anterior
        </button>

        <div className={styles.paginationDots}>
          {Array.from({ length: totalPages }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx)}
              className={`${styles.paginationDot} ${idx === currentPage ? styles.active : ''}`}
              aria-label={`Ir a página ${idx + 1}`}
            />
          ))}
        </div>

        <button 
          onClick={handleNextPage}
          disabled={currentPage === totalPages - 1}
          className={styles.paginationButton}
        >
          Siguiente
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      <div className={styles.summary}>
        <p>
          <i className="fas fa-info-circle"></i>
          Has respondido {Object.values(answers).filter(answer => answer.trim().length > 0).length} de {questions.length} preguntas
          {skippedQuestions.size > 0 && ` (${skippedQuestions.size} omitidas)`}
        </p>
      </div>
    </div>
  );
};

export default AdditionalQuestions;
